import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Bookmark, ExternalLink, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";
import SEO from "@/components/SEO";

const CATS = ["All", "Financial Crime", "Cybercrime", "Human Trafficking", "Environmental Crime", "Corruption"];

export default function Research() {
  const [papers, setPapers] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [bookmarks, setBookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iocri_bookmarks") || "[]"); } catch { return []; }
  });

  useEffect(() => { api.get("/research").then((r) => setPapers(r.data.papers || [])); }, []);

  const toggle = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    const next = bookmarks.includes(id) ? bookmarks.filter((x) => x !== id) : [...bookmarks, id];
    setBookmarks(next);
    localStorage.setItem("iocri_bookmarks", JSON.stringify(next));
  };

  const filtered = papers.filter((p) =>
    (cat === "All" || p.category === cat) &&
    (p.title.toLowerCase().includes(q.toLowerCase()) || p.abstract.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="research-page">
      <SEO
        title="Research Library"
        description="IOCRI Research Briefs on transnational organized crime, cybercrime, human trafficking, environmental crime, and corruption — with citations to UNODC, FATF, INTERPOL, Europol, IMF and other verified institutional sources."
        path="/research"
        image="institute"
      />
      <SectionTitle
        eyebrow="RESEARCH LIBRARY"
        title="Source-verified research briefs."
        subtitle="IOCRI Research Briefs are editorial summaries built on cited institutional sources. External links are provided for every reference."
      />

      <div className="mt-8 grid lg:grid-cols-[240px,1fr] gap-6">
        <aside className="space-y-2">
          <div className="text-xs font-mono tracking-widest text-gold">CATEGORY</div>
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`research-cat-${c}`}
              className={`block w-full text-left px-3 py-2 rounded-xl text-sm ${
                cat === c
                  ? "bg-gold/10 border border-gold/40 text-gold"
                  : "text-white/70 hover:text-gold border border-transparent"
              }`}
            >
              {c}
            </button>
          ))}
        </aside>

        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search briefs, topics, keywords…"
              data-testid="research-search"
              className="pl-9 bg-black border-white/10 h-11 rounded-full text-white"
            />
          </div>

          <div className="mt-6 space-y-3">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/research/${p.id}`}
                data-testid={`paper-card-${p.id}`}
                aria-label={`Read: ${p.title}`}
                className="block focus:outline-none focus:ring-2 focus:ring-gold rounded-sm doc-physics"
              >
                {/* Ghost layers physically stacked behind */}
                <div className="doc-ghost doc-ghost-2 rounded-sm" aria-hidden />
                <div className="doc-ghost doc-ghost-1 rounded-sm" aria-hidden />
                <Card className="doc-layer doc-layer-front relative bg-obsidian-soft border-white/10 rounded-sm hover:border-gold/40 transition-all cursor-pointer">
                  <CardContent className="p-6 flex flex-col md:flex-row gap-4 md:items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="evidence-chip">DOSSIER · {p.year}</span>
                        <span className="text-[10px] font-mono tracking-widest text-white/40">
                          {p.category.toUpperCase()} · {(p.type || "Research").toUpperCase()}
                        </span>
                      </div>
                      <div className="font-display text-xl md:text-2xl mt-1 text-white hover:text-gold-light transition-colors leading-tight">
                        {p.title}
                      </div>
                      <div className="text-white/50 text-xs mt-1 font-mono">
                        {p.authors.join(" · ")}
                        {p.sources && ` · ${p.sources.length} sources`}
                      </div>
                      <div className="text-white/70 text-sm mt-3">{p.abstract}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={(e) => toggle(e, p.id)}
                        data-testid={`bookmark-${p.id}`}
                        aria-label={bookmarks.includes(p.id) ? "Remove bookmark" : "Save to library"}
                        className={`h-10 w-10 rounded-xl border flex items-center justify-center ${
                          bookmarks.includes(p.id)
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-white/10 text-white/70 hover:border-gold/40"
                        }`}
                      >
                        <Bookmark className="h-4 w-4" fill={bookmarks.includes(p.id) ? "currentColor" : "none"} />
                      </button>
                      <div
                        data-testid={`read-brief-${p.id}`}
                        className="h-10 px-4 rounded-xl border border-gold/40 text-gold hover:bg-gold/10 flex items-center gap-2 text-sm"
                      >
                        <BookOpen className="h-4 w-4" /> Read Brief
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-white/5 bg-obsidian-soft p-8 text-white/50 text-sm italic text-center">
                No briefs match these filters. Try a different category or keyword.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
